import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Building2, Trash2, Calendar, FileText } from "lucide-react";

interface Company {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  contract_count?: number;
  invoice_count?: number;
  has_results?: boolean;
}

interface CompanyListProps {
  companies: Company[];
  onSelect: (id: string) => void;
  onCreate: (name: string) => void;
  onDelete: (id: string) => void;
}

export function SessionList({ companies, onSelect, onCreate, onDelete }: CompanyListProps) {
  const [newName, setNewName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = () => {
    if (!newName.trim()) return;
    onCreate(newName.trim());
    setNewName("");
    setIsCreating(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Companies</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Each company has its own contracts, invoices, and analysis results
          </p>
        </div>
        {!isCreating && (
          <Button size="sm" variant="outline" onClick={() => setIsCreating(true)}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            New Company
          </Button>
        )}
      </div>

      {isCreating && (
        <Card className="p-3 border-primary/30 bg-primary/[0.02]">
          <div className="flex gap-2">
            <Input
              placeholder="Company name (e.g. Acme Corp)"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              className="text-sm h-9"
              autoFocus
            />
            <Button size="sm" onClick={handleCreate} disabled={!newName.trim()}>
              Create
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setIsCreating(false);
                setNewName("");
              }}
            >
              Cancel
            </Button>
          </div>
        </Card>
      )}

      {companies.length === 0 && !isCreating && (
        <Card className="p-8 text-center border-dashed">
          <Building2 className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No companies yet</p>
          <p className="text-xs text-muted-foreground mt-1">
            Create a company to start uploading documents
          </p>
        </Card>
      )}

      <div className="grid gap-2">
        {companies.map((c) => (
          <Card
            key={c.id}
            className="p-3 hover:border-primary/40 transition-colors cursor-pointer group"
            onClick={() => onSelect(c.id)}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-4 h-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{c.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {new Date(c.created_at).toLocaleDateString()}
                    </span>
                    <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                      <FileText className="w-3 h-3" />
                      {c.contract_count ?? 0} contracts, {c.invoice_count ?? 0} invoices
                    </span>
                    {c.has_results && (
                      <Badge
                        variant="outline"
                        className="text-[9px] h-4 text-success border-success/30 bg-success/10"
                      >
                        Analyzed
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(c.id);
                }}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
